import { useEffect, useState, useMemo, useCallback } from "react";
import _ from "lodash";
import { IconButton, Typography } from "@material-ui/core";
import { Button } from "../../components";

import { useQueryState } from "react-router-use-location-state";
import SearchTabs from "./SearchTabs";

import { DEFAULT_ROWS_PER_PAGE } from "../../components/DataTable";
import { useWorkflowSearch } from "../../data/workflow";

import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import executionsStyles from "./executionsStyles";
import sharedStyles from "../styles";

import { Alert, AlertTitle } from "@material-ui/lab";

import BulkActionModule from "./BulkActionModule";

import { Tooltip } from "@material-ui/core";

import AgGridTable from "../../components/AgGridTable";
import DefaultLoader from "../../components/DefaultLoader";
import GlobalSearch from "./GlobalSearch";

import CustomPagination from "./CustomPagination";

import CustomLoadingOverlay from "../../components/CustomLoadingOverlay";
import {
  EMPTY_CURRENT_FILTERS,
  EMPTY_SELECTED_ROWS,
} from "../../utils/constants";

import { ReactComponent as GlobalSearchIcon } from "../../assets/GlobalSearchIcon.svg";
import {
  useCellRenderer,
  tooltipGetters,
} from "../../utils/cellRendererFactory";

const useStyles = makeStyles({
  ...executionsStyles,
  ...sharedStyles,

  inputWrapper: {
    display: "flex",
    flexDirection: "column",
  },
  inputLabel: {
    marginBottom: 1,
    marginLeft: 2,
    fontSize: 10,
    fontWeight: 600,
    color: "#000",
  },
  subheader: {
    minHeight: "150px",
    alignItems: "center",
    justifyContent: "space-evenly",
    display: "flex",
    width: "100%",
    flexWrap: "wrap",
    backgroundColor: "#f4f6ff",
  },
  // Unified icon button style to match TableControls & column selector
  iconButton: (theme) => ({
    width: 32,
    height: 32,
    padding: 0,
    borderRadius: 8,
    backgroundColor: theme?.palette?.background?.paper || "#fff",
    border: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme?.palette?.primary?.main || "#1976d2",
    transition: "box-shadow 200ms ease, transform 150ms ease",
    "&:hover": {
      backgroundColor: theme?.palette?.action?.hover || "rgba(0,0,0,0.04)",
      boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
      transform: "translateY(-1px)",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme?.palette?.primary?.main || "#1976d2"}`,
      outlineOffset: 2,
    },
    "&.Mui-disabled": {
      color: theme?.palette?.action?.disabled || "rgba(0,0,0,0.26)",
      backgroundColor:
        theme?.palette?.action?.disabledBackground || "rgba(0,0,0,0.12)",
    },
  }),
});

const DEFAULT_SORT = "startTime:DESC";
const MS_IN_DAY = 86400000;

export default function WorkflowSearch(props) {
  const permissions = props?.permissions;
  const classes = useStyles();

  const [freeText, setFreeText] = useQueryState("freeText", "");
  const [status, setStatus] = useQueryState("status", []);
  const [workflowType, setWorkflowType] = useQueryState("workflowType", []);
  const [workflowId, setWorkflowId] = useQueryState("workflowId", "");
  const [startFrom, setStartFrom] = useQueryState("startFrom", "");
  const [startTo, setStartTo] = useQueryState("startTo", "");
  const [lookback, setLookback] = useQueryState("lookback", "");
  const [requestId, setRequestid] = useQueryState("requestId", "");

  const [page, setPage] = useQueryState("page", 0);
  const [rowsPerPage, setRowsPerPage] = useQueryState(
    "rowsPerPage",
    DEFAULT_ROWS_PER_PAGE
  );
  const [sort, setSort] = useQueryState("sort", DEFAULT_SORT);
  const [queryFT, setQueryFT] = useState({ query: "", freeText: "*" });

  const enableRowSelection = useMemo(() => {
    return permissions?.workflows?.execution_actions || "False";
  }, [permissions]);

  const [globalSearchVisible, setGlobalSearchVisible] = useState(false);

  const [columnFilters, setColumnFilters] = useState({});

  const [selectedRows, setSelectedRows] = useState([]);

  const memoizedColumnFilters = useMemo(() => {
    return Object.keys(columnFilters).length === 0
      ? EMPTY_CURRENT_FILTERS
      : columnFilters;
  }, [columnFilters]);

  const timeRenderer = useCellRenderer("time");
  const simpleRenderer = useCellRenderer("simple");
  const workflowIdRenderer = useCellRenderer("workflowId");
  const jsonRenderer = useCellRenderer("json");

  // tooltip getters

  const column_permissions = useMemo(
    () =>
      permissions?.workflows?.memoizedColumnFilters || {
        input: "True",
        output: "True",
      },
    [permissions]
  );

  const columnDefs = useMemo(
    () => [
      {
        field: "startTime",
        headerName: "Start Time",
        cellRenderer: timeRenderer,
        sortable: true,
        filterable: false,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "category",
        headerName: "Type",
        cellRenderer: simpleRenderer,
        sortable: false,
        filterable: false,
        keywordField: false,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "requestId",
        headerName: "Request ID",
        cellRenderer: simpleRenderer,
        sortable: false,
        filterable: false,
        filterType: "text",
        keywordField: false,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "workflowId",
        headerName: "Workflow ID",
        cellRenderer: workflowIdRenderer,
        tooltipValueGetter: tooltipGetters.simple,
        sortable: true,
        filterable: true,
        filterType: "text",
        keywordField: true,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "workflowType",
        headerName: "Workflow Name",
        cellRenderer: simpleRenderer,
        tooltipValueGetter: tooltipGetters.simple,
        sortable: true,
        filterable: true,
        filterType: "workflowName",
        keywordField: true,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "version",
        headerName: "Version",
        cellRenderer: simpleRenderer,
        sortable: false,
        filterable: false,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "correlationId",
        headerName: "Correlation ID",
        cellRenderer: simpleRenderer,
        sortable: false,
        filterable: false,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "updateTime",
        headerName: "Update Time",
        cellRenderer: timeRenderer,
        sortable: true,
        filterable: false,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "endTime",
        headerName: "End Time",
        cellRenderer: timeRenderer,
        sortable: true,
        filterable: false,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "status",
        headerName: "Status",
        cellRenderer: simpleRenderer,
        sortable: true,
        filterable: true,
        filterType: "select",
        filterOptions: [
          "RUNNING",
          "COMPLETED",
          "FAILED",
          "TERMINATED",
          "TIMED_OUT",
          "PAUSED",
        ],
        keywordField: true,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      ...(column_permissions.input === "True"
        ? [
            {
              field: "input",
              headerName: "Input",
              cellRenderer: jsonRenderer,
              tooltipValueGetter: tooltipGetters.json,
              sortable: false,
              filterable: false,
              width: 250,
              minWidth: 250,
              flex: 1,
            },
          ]
        : []),
      ...(column_permissions.output === "True"
        ? [
            {
              field: "output",
              headerName: "Output",
              cellRenderer: jsonRenderer,
              tooltipValueGetter: tooltipGetters.simple,
              sortable: false,
              filterable: false,
              width: 250,
              minWidth: 250,
              flex: 1,
            },
          ]
        : []),
      {
        field: "reasonForIncompletion",
        headerName: "Reason For Incompletion",
        cellRenderer: simpleRenderer,
        tooltipValueGetter: tooltipGetters.json,
        sortable: true,
        filterable: true,
        filterType: "text",
        keywordField: true,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "executionTime",
        headerName: "Execution Time",
        cellRenderer: simpleRenderer,
        sortable: true,
        filterable: false,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "event",
        headerName: "Event",
        cellRenderer: simpleRenderer,
        sortable: false,
        filterable: false,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "failedReferenceTaskNames",
        headerName: "Failed Reference Task Names",
        cellRenderer: simpleRenderer,
        tooltipValueGetter: tooltipGetters.simple,
        sortable: true,
        filterable: true,
        filterType: "text",
        keywordField: true,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "externalInputPayloadStoragePath",
        headerName: "External Input Payload Storage Path",
        cellRenderer: simpleRenderer,
        sortable: false,
        filterable: false,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "externalOutputPayloadStoragePath",
        headerName: "External Output Payload Storage Path",
        cellRenderer: simpleRenderer,
        sortable: false,
        filterable: false,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
      {
        field: "priority",
        headerName: "Priority",
        cellRenderer: simpleRenderer,
        sortable: false,
        filterable: false,
        width: 250,
        minWidth: 250,
        flex: 1,
      },
    ],
    [
      timeRenderer,
      simpleRenderer,
      workflowIdRenderer,
      jsonRenderer,
      tooltipGetters,
      column_permissions,
    ]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
    }),
    []
  );

  const defaultVisibleColumns = useMemo(
    () => [
      "startTime",
      "requestId",
      "workflowType",
      "workflowId",
      "endTime",
      "status",
    ],
    []
  );

  const {
    data: resultObj,
    error,
    isFetching,
    refetch,
  } = useWorkflowSearch({
    page: page + 1, // Convert to 1-based for API
    rowsPerPage,
    sort,
    query: queryFT.query,
    freeText: queryFT.freeText,
  });

  const handlePageChange = useCallback(
    (newPage) => {
      // Check if this would exceed the total pages
      const totalPages = Math.ceil((resultObj?.totalHits || 0) / rowsPerPage);

      // Validate page number
      if (newPage < 0) {
        newPage = 0;
      } else if (newPage >= totalPages) {
        newPage = Math.max(0, totalPages - 1);
      }

      setPage(newPage);
    },
    [setPage, resultObj?.totalHits, rowsPerPage]
  );

  // Handle rows per page change
  const handlePageSizeChange = useCallback(
    (newPageSize) => {
      setRowsPerPage(newPageSize);
      setPage(0); // Reset to first page when changing page size
    },
    [setRowsPerPage, setPage]
  );

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const buildQuery = useCallback(() => {
    const clauses = [];
    if (!_.isEmpty(workflowType)) {
      clauses.push(`workflowType IN (${workflowType.join(",")})`);
    }
    if (!_.isEmpty(workflowId)) {
      clauses.push(`workflowId="${workflowId}"`);
    }
    if (!_.isEmpty(status)) {
      clauses.push(`status IN (${status.join(",")})`);
    }
    if (!_.isEmpty(lookback)) {
      clauses.push(`startTime>${new Date().getTime() - lookback * MS_IN_DAY}`);
    }
    if (!_.isEmpty(startFrom)) {
      clauses.push(`startTime>${new Date(startFrom).getTime()}`);
    }
    if (!_.isEmpty(startTo)) {
      clauses.push(`startTime<${new Date(startTo).getTime()}`);
    }
    if (!_.isEmpty(requestId)) {
      clauses.push(`requestId="${requestId}"`);
    }

    Object.entries(memoizedColumnFilters).forEach(([field, value]) => {
      if (field && value) {
        if (["startTime", "endTime", "updateTime"].includes(field)) {
          clauses.push(`${field}>${new Date(value).getTime()}`);
        } else if (field === "status") {
          const statusArr = Array.isArray(value) ? value : [value];
          clauses.push(
            `status IN (${statusArr
              .map((s) => String(s).toLowerCase())
              .join(",")})`
          );
        } else if (field === "workflowType") {
          const typeArr = Array.isArray(value) ? value : [value];
          clauses.push(
            `workflowType IN (${typeArr
              .map((v) => String(v).toLowerCase())
              .join(",")})`
          );
        } else {
          clauses.push(`${field}="${value}"`);
        }
      }
    });
    return {
      query: clauses.join(" AND "),
      freeText: _.isEmpty(freeText) ? "*" : freeText,
    };
  }, [
    workflowType,
    workflowId,
    status,
    lookback,
    startFrom,
    startTo,
    requestId,
    freeText,
    memoizedColumnFilters,
  ]);

  const debouncedBuildQuery = useMemo(
    () =>
      _.debounce(() => {
        const queryResult = buildQuery();
        setQueryFT((prev) => {
          if (_.isEqual(prev, queryResult)) {
            return prev;
          }
          return queryResult;
        });
      }, 300),
    [buildQuery, setQueryFT]
  );

  useEffect(() => {
    debouncedBuildQuery();
    return () => debouncedBuildQuery.cancel();
  }, [
    workflowType,
    workflowId,
    status,
    lookback,
    startFrom,
    startTo,
    requestId,
    freeText,
    debouncedBuildQuery,
    memoizedColumnFilters,
  ]);

  const handleGlobalSearch = useCallback(
    (searchParams) => {
      // Update the search parameters
      setWorkflowType(searchParams.workflowType || []);
      setWorkflowId(searchParams.workflowId || "");
      setStatus(searchParams.status || []);
      setStartFrom(searchParams.startFrom || "");
      setStartTo(searchParams.startTo || "");
      setLookback(searchParams.lookback || "");
      setFreeText(searchParams.freeText || "");
      setRequestid(searchParams.requestId || "");

      // Clear any previous selections when running a new global search
      setSelectedRows([]);

      // Reset page to first page
      setPage(0);

      // Query will be rebuilt via useEffect and trigger a refetch
    },
    [
      setWorkflowType,
      setWorkflowId,
      setStatus,
      setStartFrom,
      setStartTo,
      setLookback,
      setFreeText,
      setRequestid,
      setPage,
      setSelectedRows,
    ]
  );

  const handleColumnFilterChanged = useCallback(
    (fieldName, filterValue) => {
      // Create new filters object
      const newFilters = {
        ...memoizedColumnFilters,
      };

      if (filterValue) {
        newFilters[fieldName] = filterValue;
      } else {
        delete newFilters[fieldName];
      }

      setColumnFilters(newFilters);

      // Clear selections when server-side column filters change
      // setSelectedRows([]);

      // Reset to first page when filters change
      setPage(0);
    },
    [memoizedColumnFilters, setPage]
  );

  const handleResetAllFilters = useCallback(() => {
    // Reset all search filters to default values
    setWorkflowType([]);
    setWorkflowId("");
    setStatus([]);
    setStartFrom("");
    setStartTo("");
    setLookback("");
    setFreeText("");
    setRequestid("");
    setColumnFilters({});
    setRowsPerPage(DEFAULT_ROWS_PER_PAGE);

    // Reset to first page
    setPage(0);

    // Optionally, reset the sort to default
    setSort(DEFAULT_SORT);

    // Also clear any selected rows on full reset
    setSelectedRows([]);

    refetch();
  }, [
    setWorkflowType,
    setWorkflowId,
    setStatus,
    setStartFrom,
    setStartTo,
    setLookback,
    setFreeText,
    setRequestid,
    setPage,
    setSort,
    setSelectedRows,
    refetch,
  ]);

  const totalHits = resultObj?.totalHits || 0;

  const memoizedSelectedRows = useMemo(() => {
    return selectedRows.length === 0 ? EMPTY_SELECTED_ROWS : selectedRows;
  }, [selectedRows]);

  const renderPagination = useCallback(() => {
    if (!resultObj || error) return null;

    return (
      <CustomPagination
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalHits}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[5, 15, 30, 50, 100, 500]}
      />
    );
  }, [
    page,
    rowsPerPage,
    totalHits,
    handlePageChange,
    handlePageSizeChange,
    resultObj,
    error,
  ]);

  const toggleGlobalSearch = useCallback(() => {
    setGlobalSearchVisible((prev) => !prev);
  }, []);

  const closeGlobalSearch = useCallback(() => {
    setGlobalSearchVisible(false);
  }, []);

  const memoizedInitialValues = useMemo(
    () => ({
      workflowType,
      workflowId,
      status,
      startFrom,
      startTo,
      lookback,
      freeText,
      requestId,
    }),
    [
      workflowType,
      workflowId,
      status,
      startFrom,
      startTo,
      lookback,
      freeText,
      requestId,
    ]
  );

  const GlobalSearchButton = useMemo(
    () => (
      <Tooltip title="Global Search">
        <IconButton
          className={classes.iconButton}
          color="primary"
          onClick={toggleGlobalSearch}
          aria-label="Global search"
        >
          <GlobalSearchIcon />
        </IconButton>
      </Tooltip>
    ),
    [toggleGlobalSearch]
  );

  const memoizedVisibleGlobalSearch = useMemo(() => {
    if (!globalSearchVisible) return null;

    return (
      <div style={{ flexShrink: 0 }}>
        <GlobalSearch
          visible={true} // Always true when rendered
          onClose={closeGlobalSearch}
          onSearch={handleGlobalSearch}
          initialValues={memoizedInitialValues}
        />
      </div>
    );
  }, [
    globalSearchVisible,
    closeGlobalSearch,
    handleGlobalSearch,
    memoizedInitialValues,
  ]);
  const handleRowSelectionChanged = useCallback(
    (currentPageSelectedRows) => {
      const currentPageData = resultObj?.results || [];
      const currentPageIds = new Set(
        currentPageData.map((r) => r?.workflowId).filter(Boolean)
      );

      // Keep selections from other pages intact
      const preservedFromOtherPages = selectedRows.filter(
        (r) => !currentPageIds.has(r.workflowId)
      );

      // Merge with current page selections, de-duplicated by workflowId
      const mergedMap = new Map(
        preservedFromOtherPages.map((r) => [r.workflowId, r])
      );
      currentPageSelectedRows.forEach((r) => {
        if (r?.workflowId) mergedMap.set(r.workflowId, r);
      });
      const merged = Array.from(mergedMap.values());

      // Only update if selection actually changed
      const prevIds = selectedRows.map((r) => r.workflowId).sort();
      const nextIds = merged.map((r) => r.workflowId).sort();
      if (JSON.stringify(prevIds) !== JSON.stringify(nextIds)) {
        setSelectedRows(merged);
      }
    },
    [selectedRows, resultObj]
  );

  const bulkActionComponent = useMemo(() => {
    return selectedRows.length > 0 ? (
      <BulkActionModule
        selectedRows={selectedRows}
        onActionComplete={() => {
          handleResetAllFilters();
        }}
      />
    ) : null;
  }, [selectedRows, handleResetAllFilters]);

  const handleSortChange = useCallback(
    (newSort) => {
      setSort(newSort || DEFAULT_SORT);
      setPage(0); // Reset to first page when sorting changes
    },
    [setSort, setPage]
  );

  // Clear selected rows handler to be passed to the grid controls
  const handleClearSelectedRows = useCallback(() => {
    setSelectedRows([]);
  }, []);

  const memoizedGridOptions = useMemo(
    () => ({
      suppressCellFocus: false,
      animateRows: true,
      loadingOverlayComponent: "agGridLoadingOverlay",
      components: {
        agGridLoadingOverlay: CustomLoadingOverlay,
      },
      loadingOverlayComponentParams: {
        loadingMessage: "Loading executions...",
      },
      overlayNoRowsTemplate:
        '<span class="ag-overlay-no-rows-center">No data found</span>',
      domLayout: "normal",
      suppressHorizontalScroll: false,
      suppressVerticalScroll: false,
    }),
    []
  );

  return (
    // 1. root full‐viewport flex, reserve 50px for footer
    <div
      className={clsx([classes.wrapper])}
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        paddingBottom: "90px",
      }}
    >
      {/* 2. content container grows and hides overflow */}
      <div
        className={clsx([classes.contentContainer])}
        style={{
          flex: "1 1 auto",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {<SearchTabs permissions={permissions} />}
        {/* Show LinearProgress when fetching */}

        {error && (
          <Alert
            severity="error"
            style={{ margin: "10px" }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleResetAllFilters}
              >
                Return to first page
              </Button>
            }
          >
            <AlertTitle>Error loading data</AlertTitle>
            {error.response?.data?.message ||
              error.message ||
              "An unknown error occurred"}
            <Typography
              variant="caption"
              display="block"
              style={{ marginTop: "8px" }}
            >
              This could be due to requesting data beyond what's available or a
              server issue.
            </Typography>
          </Alert>
        )}

        <DefaultLoader
          displayModal={isFetching && !resultObj && !error}
          message="Loading Executions data..."
          contentStyle={{ width: "300px" }}
        />

        {resultObj && !error && (
          // 3. replace fixed‐height block with flex‐grow wrapper
          <div
            style={{
              flex: "1 1 auto",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {memoizedVisibleGlobalSearch}
            {/* 4. grid wrapper flex‐grow */}
            <div
              style={{
                flex: "1 1 auto",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                minHeight: 0,
              }}
            >
              <AgGridTable
                tableId="workflow-search-table"
                rowData={resultObj.results}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                clientSidePagination={false}
                gridOptions={memoizedGridOptions}
                defaultVisibleColumns={defaultVisibleColumns}
                handleRefreshClick={handleRefresh}
                loading={isFetching}
                error={error}
                GlobalSearch={GlobalSearchButton}
                enableRowSelection={enableRowSelection}
                onRowSelectionChanged={handleRowSelectionChanged}
                selectedRows={memoizedSelectedRows}
                bulkActionComponent={bulkActionComponent}
                currentSort={sort}
                onSortChanged={handleSortChange}
                currentFilters={memoizedColumnFilters}
                onFilterChanged={handleColumnFilterChanged}
                resetServerFilters={true}
                handleResetFilters={handleResetAllFilters}
                tooltipShowDelay={500}
                onClearSelectedRows={handleClearSelectedRows}
              />
            </div>
          </div>
        )}
      </div>

      {/* ...existing fixed-bottom pagination div... */}
      {resultObj && !error && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            height: "50px",
            backgroundColor: "#fff",
            zIndex: 1000,
            boxShadow: "0px -2px 4px rgba(0,0,0,0.05)",
            padding: "0 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          {renderPagination()}
        </div>
      )}
    </div>
  );
}
